import { onMounted, onUnmounted, ref, computed } from 'vue'

/**
 * Generic composable for managing state based on URL hash
 * Supports both object mapping and array of objects with anchorId property
 * @param {Object|Array} hashConfig - Hash configuration:
 *   - Object: Direct hash-to-data mapping, e.g. { 'netbank': { id: 'login-overlay' } }
 *   - Array: Objects with 'anchorId' property, e.g. [{ anchorId: 'terms', title: '...', description: '...' }]
 * @returns {Object} - { activeHash, activeData, setHash, clearHash, isActive }
 */
export function useHashToToggle(hashConfig) {
    // Normalize input to object mapping
    const hashMap = Array.isArray(hashConfig)
        ? Object.fromEntries(hashConfig.map(item => [item.anchorId, item]))
        : hashConfig

    const activeHash = ref(null)
    
    /**
     * Clears the hash from the current URL
     */
    const clearHash = () => {
        activeHash.value = null
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    
    /**
     * Sets a hash in the current URL
     * @param {string} hash - The hash to set (without the # symbol)
     */
    const setHash = (hash) => {
        if (hashMap[hash]) {
            activeHash.value = hash
            window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + hash)
        }
    }
    
    /**
     * Returns the data object for the currently active hash
     */
    const activeData = computed(() => {
        return activeHash.value ? hashMap[activeHash.value] : null
    })
    
    /**
     * Checks if a specific hash is currently active
     * @param {string} hash - The hash to check
     */
    const isActive = (hash) => {
        return activeHash.value === hash
    }
    
    const handleHashChange = () => {
        const hash = window.location.hash.replace('#', '')
        if (hash && hashMap[hash]) {
            activeHash.value = hash
        } else if (!hash) {
            activeHash.value = null
        }
    }
    
    onMounted(() => {
        handleHashChange()
        window.addEventListener('hashchange', handleHashChange, false)
    })
    
    onUnmounted(() => {
        window.removeEventListener('hashchange', handleHashChange, false)
    })
    
    return {
        activeHash,
        activeData,
        setHash,
        clearHash,
        isActive
    }
}
